import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { call } from "../../api/client.js";
import { uploadImages } from "../../api/upload.api.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Select } from "../../components/ui/Select.jsx";

/**
 * Bulk-import gallery photos. Pick a section (Interior / Exterior), drop
 * N images, and we upload them in one round-trip then create one
 * GalleryImage row per image, titled from the file name.
 *
 * Modelled on BulkImportFacadesPage — same upload + create-loop pattern,
 * but the create step uses the dedicated POST /admin/gallery/bulk
 * endpoint so all rows land in a single insertMany.
 */
const SECTIONS = [
  { value: "interior", label: "Interior" },
  { value: "exterior", label: "Exterior" },
];

export default function BulkImportGalleryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [section, setSection] = useState("interior");
  const [titlePrefix, setTitlePrefix] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    document.title = "Bulk import gallery · Admin";
  }, []);

  useEffect(() => {
    return () => {
      previews.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePicked = (selected) => {
    const arr = Array.from(selected ?? []);
    if (arr.length === 0) return;
    setFiles((prev) => [...prev, ...arr]);
    setPreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAt = (i) => {
    setPreviews((prev) => {
      const url = prev[i];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, idx) => idx !== i);
    });
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const titleFromFilename = (file) => {
    const base = file.name.replace(/\.[^/.]+$/, "");
    const cleaned = base
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const words = cleaned
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    return titlePrefix
      ? `${titlePrefix.trim()} — ${words}`
      : words || "Untitled photo";
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error("Add at least one image.");
      return;
    }

    setBusy(true);
    try {
      setProgress({ stage: "Uploading images", done: 0, total: files.length });
      const uploaded = await uploadImages(files, "gallery");
      if (uploaded.length !== files.length) {
        toast.warning(`${uploaded.length} of ${files.length} images uploaded.`);
      }
      if (uploaded.length === 0) {
        toast.error("Nothing uploaded.");
        return;
      }

      setProgress({ stage: "Creating rows", done: 0, total: uploaded.length });
      const items = uploaded.map((u, i) => ({
        title: titleFromFilename(files[i] ?? files[0]),
        image: u.url,
      }));

      const result = await call("post", "/admin/gallery/bulk", {
        data: { section, items },
      });
      const created = result?.created ?? items.length;
      setProgress({ stage: "Creating rows", done: created, total: uploaded.length });

      toast.success(`Imported ${created} ${section} photo${created === 1 ? "" : "s"}.`);
      previews.forEach((u) => URL.revokeObjectURL(u));
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      navigate("/gallery");
    } catch (err) {
      const details = err?.response?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        toast.error(`${details[0].path}: ${details[0].message}`);
      } else {
        toast.error(err?.response?.data?.message ?? "Bulk import failed");
      }
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <FormShell
      title="Bulk import gallery photos"
      backTo="/gallery"
      backLabel="Back to gallery"
      onSubmit={(e) => {
        e?.preventDefault?.();
        handleSubmit();
      }}
      primaryAction={
        <Button onClick={handleSubmit} loading={busy}>
          Import{" "}
          {files.length > 0 ? `${files.length} photo${files.length === 1 ? "" : "s"}` : ""}
        </Button>
      }
      secondaryAction={
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate("/gallery")}
          disabled={busy}
        >
          Cancel
        </Button>
      }
      sidebar={
        <FormSection title="How this works">
          <p className="text-sm text-text-secondary">
            Pick a section, optionally set a title prefix, then drop or pick
            multiple images.
          </p>
          <p className="mt-3 text-sm text-text-secondary">
            One photo row will be created per image, titled from the file name
            (snake-case &amp; dashes converted to Title Case). Run the importer
            again to add to the other section.
          </p>
          {progress && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/10 p-3 text-xs text-accent">
              <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
              <span>
                {progress.stage} — {progress.done} / {progress.total}
              </span>
            </div>
          )}
        </FormSection>
      }
    >
      <FormSection title="Section">
        <Select
          label="Section"
          required
          value={section}
          onChange={(e) => setSection(e.target.value)}
          disabled={busy}
        >
          {SECTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        <Input
          label="Title prefix (optional)"
          value={titlePrefix}
          onChange={(e) => setTitlePrefix(e.target.value)}
          hint='e.g. "Kitchen" — prepended to each title generated from the filename.'
          disabled={busy}
        />
      </FormSection>

      <FormSection
        title="Images"
        description="Pick multiple — one row will be created per image."
      >
        <div className="rounded-xl border border-dashed border-border bg-bg-elevated-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-text-secondary">
              {files.length === 0
                ? "No images yet. Click 'Pick images' to start."
                : `${files.length} image${files.length === 1 ? "" : "s"} ready to import.`}
            </p>
            <Button
              type="button"
              variant="secondary"
              leftIcon={<ImagePlus className="h-4 w-4" />}
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              Pick images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => handlePicked(e.target.files)}
            />
          </div>

          {previews.length > 0 && (
            <ul className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {previews.map((src, i) => (
                <li
                  key={`${src}-${i}`}
                  className="relative aspect-square overflow-hidden rounded-md border border-border-subtle bg-bg-elevated"
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    disabled={busy}
                    className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-bg-base/85 text-text-primary hover:bg-danger hover:text-white disabled:opacity-50"
                    aria-label={`Remove ${files[i]?.name ?? "image"}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <p className="absolute inset-x-0 bottom-0 truncate bg-bg-base/85 px-1 py-0.5 text-[10px] text-text-secondary">
                    {files[i]?.name}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </FormSection>
    </FormShell>
  );
}
